import { fetchClinicStudiesSupport } from '@/entities/study';
import { handleSubmitSupportRequest } from '@/entities/support';
import { INVALID_EMAIL_ERROR_MESSAGE, LOADING_TYPES } from '@/shared/config';
import { isNumberByRegex, validateEmail, showMessageError, showMessageSuccess } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect } from 'react';
import { t } from 'ttag';
import type { ReportRequestProps, ReportRequestState } from './reportRequest.types';

/**
 * Builds a study id tag for the report-request form's multi-value select. Uses the typed label as both label and value and starts with an empty errorMessage and an undefined study id, which validation fills in later.
 *
 * @param label - Study friendly id typed by the user.
 * @returns A study id tag option.
 */
const createOption = (label: any): any => ({
  label,
  value: label,
  errorMessage: '',
  id: undefined,
});

/**
 * Holds the form state of the report support request: study id tags, reply emails, message and
 * errors. Validates each tagged study id against the facility's studies, clears the form and
 * revalidates the tags when the facility changes, and submits one content entry per study through
 * handleSubmitSupportRequest.
 *
 * @param props - Form props: category, selectedFacility, setLoadingPage and backToPrevious.
 * @returns An object carrying the form state and the change, send, email, paste, study id input,
 *   tag, key-down and click-outside handlers.
 */
export const useReportRequest = ({
  category,
  selectedFacility,
  setLoadingPage,
  backToPrevious,
}: ReportRequestProps) => {
  const [state, setState] = useMergeState<ReportRequestState>({
    emailList: [],
    emailInputValue: '',
    message: '',
    errorObject: {},
    isSaving: false,
    multiSearchValue: [],
    inputValue: '',
  });

  /**
   * Looks up one clinic study by its friendly id so a report request can be validated. Queries the support study list with includeRma and the selected facility and returns the first match; a failure is logged, the saving flag is cleared and an empty object is returned.
   *
   * @param studyFid - Friendly study id to look up.
   * @returns The matching study record, or an empty object on failure.
   */
  const fetchStudyId = async (studyFid: any) => {
    try {
      const queryFilter = {
        studyFid: studyFid ? Number(studyFid) : undefined,
        includeRma: true,
        facilityId: selectedFacility?.value as string,
        viewAll: true,
      };
      const result = await fetchClinicStudiesSupport(queryFilter, 1);
      return result[0];
    } catch (error) {
      console.error('Failed to check study ID: ', error);
      setState({ isSaving: false });
      return {};
    }
  };

  /**
   * Validates every study id tag entered on the report request and annotates each one with its error state. Fetches the studies in parallel, matches them by friendlyId, then stamps each tag with its study id, with an open-request message when the study already has a clinicTicket, or with an invalid-study-id message when nothing matched, writing the annotated tags back to state.
   *
   * @param multiSearchValue - Study id tags entered so far.
   * @returns A promise resolving once the tag list and the saving flag have been updated in state.
   */
  const checkStudy = async (multiSearchValue: any) => {
    if (!_.isEmpty(multiSearchValue)) {
      setState({ isSaving: true });
      const clonedMultiSearchValue = _.cloneDeep(multiSearchValue);
      const promises: any = [];
      _.forEach(clonedMultiSearchValue, (item) => {
        promises.push(fetchStudyId(Number(item.value)));
      });
      const studyInfoArray: any = await Promise.all(promises);
      _.forEach(clonedMultiSearchValue, (item) => {
        /**
         * Locates the fetched study whose friendlyId matches the numeric value of the tag currently being validated. Compares Number-coerced friendlyId against the tag value across the studyInfoArray resolved from the parallel study lookups.
         *
         * @returns Matching study record, or undefined when no fetched study has that friendly ID.
         */
        const foundItem = _.find(
          studyInfoArray,
          (x) => Number(x?.friendlyId) === Number(item?.value),
        );
        if (foundItem) {
          if (foundItem.clinicTicket?.id) {
            _.assign(item, {
              errorMessage: 'This study currently has an open request!',
              id: foundItem?.id,
            });
          } else {
            _.assign(item, { errorMessage: '', id: foundItem?.id });
          }
        } else {
          _.assign(item, { errorMessage: 'Invalid study ID', id: undefined });
        }
      });
      setState({ multiSearchValue: clonedMultiSearchValue, isSaving: false });
    }
  };

  /**
   * Stores a field of the report-request form, such as the message body. The value is written under the supplied field name and that field's entry is dropped from the error object.
   *
   * @param name - Field name used as the state key.
   * @param value - Value to store.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject[name];
    setState({ [name]: value, errorObject: cloneErrorObject });
  };

  /**
   * Submits the report request for the tagged studies. A malformed pending reply email is rejected with an inline error first, otherwise the submitting loader is shown and the request is posted with the category, joined reply emails, one content entry per tagged study id, the trimmed message and the selected facility, followed by a success toast and a return to the previous page; failures log, show an error toast and clear the saving flag.
   *
   * @returns A promise resolving once the toast feedback and the navigation have been triggered.
   */
  const onClickSendMessage = async () => {
    setState({ isSaving: true });
    const errorObject = {};
    const isValidCurrentInput = !state.emailInputValue || validateEmail(state.emailInputValue);
    if (!isValidCurrentInput) {
      _.assign(errorObject, { invalidEmails: INVALID_EMAIL_ERROR_MESSAGE });
    }
    if (!_.isEmpty(errorObject)) {
      setState({
        errorObject,
        isSaving: false,
      });
    } else {
      try {
        setLoadingPage(LOADING_TYPES.SUBMITTING);
        await handleSubmitSupportRequest({
          category,
          replyEmail: _.join(state.emailList, ','),
          contents: _.map(state.multiSearchValue, (item) => ({
            study: item?.id,
          })),
          message: state.message.trim(),
          appName: 'Bioflux',
          facilityId: selectedFacility?.value as string,
        });
        showMessageSuccess({
          content: t`We have received your request and will email you back when it has been completed`,
        });
        setLoadingPage(false);
        backToPrevious();
      } catch (error) {
        console.error('Failed to send support email: ', error);
        showMessageError({ content: t`Failed to send your message` });
        setState({
          isSaving: false,
        });
      }
    }
  };

  /**
   * Replaces the reply email list of the report request with the addresses accepted by the multi-email input. Clears the pending email text and the invalid-email error at the same time.
   *
   * @param value - Accepted reply email addresses.
   * @returns Nothing.
   */
  const onChangeEmailList = (value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.invalidEmails;
    setState({ emailList: value, emailInputValue: '', errorObject: cloneErrorObject });
  };

  /**
   * Tracks the reply email text typed but not yet committed on the report request. Stores it as emailInputValue and clears the invalid-email error.
   *
   * @param value - Current text in the email input.
   * @returns Nothing.
   */
  const onInputChange = (value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.invalidEmails;
    setState({ emailInputValue: value, errorObject: cloneErrorObject });
  };

  /**
   * Accepts a pasted block of study ids on the report request. Splits the clipboard text on spaces and commas, keeps only numeric ids that are not already tagged, deduplicates them into options, appends them to the tag list and validates the result.
   *
   * @param e - Paste event carrying the clipboard text.
   * @returns Nothing.
   */
  const handleOnPaste = (e: any) => {
    e.preventDefault();
    const splitIds = e.clipboardData.getData('text').split(' ').join(',').split(',');
    /**
     * Narrows the pasted ID strings down to the ones worth turning into tags. Keeps an entry only when it matches the numeric regex and no existing tag in multiSearchValue already holds that value.
     *
     * @returns Pasted ID strings that are numeric and not already tagged.
     */
    const filteredData = _.filter(
      splitIds,
      (id) => isNumberByRegex(id) && !_.find(state.multiSearchValue, (x) => x.value === id),
    );
    /**
     * Derives the study-ID tag options for the pasted clipboard content. Deduplicates the already-filtered ID strings and maps each one into an option carrying label, value, empty errorMessage and undefined id.
     *
     * @returns Array of newly created study-ID tag options.
     */
    const valueArr = _.map(_.uniq(filteredData), (item) => createOption(item));
    const newMultiSearchValue = [...state.multiSearchValue, ...valueArr];
    setState({ multiSearchValue: newMultiSearchValue, inputValue: '' });
    checkStudy(newMultiSearchValue);
  };

  /**
   * Keeps the report-request study id input restricted to digits while typing. A non-empty value failing the numeric regex is discarded, anything else is stored as inputValue.
   *
   * @param inputValue - Text typed into the study id select.
   * @returns Nothing.
   */
  const onInputChangeMultiIdSearch = (inputValue: any) => {
    if (inputValue !== '' && !isNumberByRegex(inputValue)) {
      return;
    }
    setState({ inputValue });
  };

  /**
   * Stores the study id tags of the report request after the select changes, including when tags are cleared. A null value is replaced by an empty array so the tag list stays iterable.
   *
   * @param value - Tag list reported by the select, possibly null.
   * @returns Nothing.
   */
  const onChangeMultiIdSearch = (value: any) => {
    //* Trigger when clear tag
    //* Prevent null value so that multiSearchValue can be iterable
    setState({ multiSearchValue: value || [] });
  };

  /**
   * Turns the typed study id into a tag when comma or space is pressed in the report-request id select. An empty input is ignored, the default key handling is prevented, and an id that is not yet tagged is appended as an option and validated, otherwise only the input is cleared.
   *
   * @param e - Keyboard event from the creatable select.
   * @returns Nothing.
   */
  const onKeyDownCreatableSelect = (e: any) => {
    if (!state.inputValue) {
      return;
    }
    if (e.keyCode === 188 || e.keyCode === 32) {
      e.preventDefault();
      if (!_.find(state.multiSearchValue, (x) => Number(x.value) === Number(state.inputValue))) {
        const value = createOption(state.inputValue);
        const newMultiSearchValue = [...state.multiSearchValue, value];
        setState({ multiSearchValue: newMultiSearchValue, inputValue: '' });
        checkStudy(newMultiSearchValue);
      } else {
        setState({ inputValue: '' });
      }
    }
  };

  /**
   * Commits the study id still sitting in the report-request input when focus leaves the select. A non-empty id that is not already tagged becomes an option, is appended to the tag list and validated, otherwise only the input text is cleared.
   *
   * @returns Nothing.
   */
  const handleClickOutside = () => {
    if (
      !_.find(state.multiSearchValue, (x) => Number(x.value) === Number(state.inputValue)) &&
      state.inputValue !== ''
    ) {
      const value = createOption(state.inputValue);
      const newMultiSearchValue = [...state.multiSearchValue, value];
      setState({ multiSearchValue: newMultiSearchValue, inputValue: '' });
      checkStudy(newMultiSearchValue);
    } else {
      setState({ inputValue: '' });
    }
  };

  useEffect(() => {
    setState({
      emailList: [],
      emailInputValue: '',
      message: '',
      errorObject: {},
    });
    checkStudy(state.multiSearchValue);
  }, [selectedFacility]);

  return {
    state,
    onChange,
    onClickSendMessage,
    onChangeEmailList,
    onInputChange,
    handleOnPaste,
    onInputChangeMultiIdSearch,
    onChangeMultiIdSearch,
    onKeyDownCreatableSelect,
    handleClickOutside,
  };
};
